// frontend/src/components/billing/billing-method.tsx
// Read DESIGN.md before modifying this file.
// Phase 2 (spec 2026-06-18): saved-card display + native card management.
// Reads GET /organizations/:orgId/billing/payment-methods via Query and renders
// an inline Retry on failure — never the global error page. Set Default /
// Remove / Add Card all invalidate the same payment-methods key so the list
// refreshes without a manual reload.
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  usePaymentMethodsQuery,
  usePaymentMethodsInvalidate,
  useSetDefaultCard,
  useRemoveCard,
  useSubscriptionQuery,
} from './queries';
import { AddCardDialog } from './add-card-dialog';
import { emit } from '@/lib/analytics';

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function fmtExpiry(month: number | null, year: number | null): string | null {
  if (month == null || year == null) return null;
  return `Exp ${String(month).padStart(2, '0')}/${year}`;
}

export function BillingMethod({ orgId }: { orgId: string }) {
  const methodsQuery = usePaymentMethodsQuery(orgId);
  const invalidateMethods = usePaymentMethodsInvalidate(orgId);
  const setDefaultCard = useSetDefaultCard(orgId);
  const removeCard = useRemoveCard(orgId);
  // AIT-505 — a paused org's Add Card here resumes the plan (same server
  // rule as everywhere else); the resumePreview quotes exactly what that
  // charges, so the notice never asks for a card without saying what it does.
  const subscriptionQuery = useSubscriptionQuery(orgId);
  const subscription = subscriptionQuery.data ?? null;
  const resumePreview = subscription?.status === 'paused' ? subscription.resumePreview : undefined;
  // Review round 3 fix (P2) — a paused org with no resumePreview still needs
  // truthful copy (never the default "won't be charged" text): fall back to
  // a generic resume-and-charge notice with no amount.
  const addCardNotice =
    subscription?.status === 'paused'
      ? resumePreview
        ? `${subscription?.hasEverPaid ? 'Your subscription is paused.' : 'Your trial ended.'} Saving a card resumes ${resumePreview.name} and charges $${Math.round(resumePreview.priceCents / 100)} now.`
        : subscription?.hasEverPaid
          ? 'Your subscription is paused. Saving a card resumes your plan and charges it now.'
          : 'Your trial ended. Saving a card resumes your plan and charges it now.'
      : undefined;
  const [addOpen, setAddOpen] = useState(false);

  // AIT-505 (review F12) — the one opener on this surface; fires once per
  // open, mirroring the Plans page's single emit site.
  useEffect(() => {
    if (addOpen) emit('add_card_opened', { source: 'settings' });
  }, [addOpen]);

  const methods = methodsQuery.data ?? [];
  const loading = methodsQuery.isPending || methodsQuery.isFetching;
  const error = methodsQuery.isError;

  function setDefault(id: string) {
    setDefaultCard.mutate(
      { paymentMethodId: id },
      {
        onSuccess: () => toast.success('Default card updated.'),
        onError: () =>
          toast.error('Could not update default. Please try again.'),
      },
    );
  }

  function remove(id: string) {
    removeCard.mutate(
      { paymentMethodId: id },
      {
        onSuccess: () => toast.success('Card removed.'),
        onError: (err) =>
          toast.error(err.userMessage ?? 'Could not remove the card.'),
      },
    );
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold tracking-tight">Billing Method</h3>
      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading card...
        </div>
      ) : error ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Unable to load saved card.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void methodsQuery.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : methods.length === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            No card on file.
          </p>
          <Button size="sm" onClick={() => setAddOpen(true)}>
            Add Card
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <ul className="divide-y rounded-2xl border">
            {methods.map((pm) => (
              <li
                key={pm.id}
                className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-sm"
              >
                <div className="flex items-center gap-3">
                  <span className="font-medium">
                    {pm.brand ? capitalize(pm.brand) : 'Card'}
                  </span>
                  {pm.last4 && (
                    <span className="tabular-nums text-muted-foreground">
                      •••• {pm.last4}
                    </span>
                  )}
                  {fmtExpiry(pm.expMonth, pm.expYear) && (
                    <span className="text-muted-foreground">
                      {fmtExpiry(pm.expMonth, pm.expYear)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {pm.isDefault && <Badge variant="secondary">Default</Badge>}
                  {!pm.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDefault(pm.id)}
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(pm.id)}
                  >
                    Remove
                  </Button>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex justify-end">
            <Button size="sm" onClick={() => setAddOpen(true)}>
              Add Card
            </Button>
          </div>
        </div>
      )}
      <AddCardDialog
        orgId={orgId}
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={() => {
          void invalidateMethods();
        }}
        notice={addCardNotice}
        mintVars={{ interval: resumePreview?.interval, source: 'settings' }}
      />
    </div>
  );
}
